// components/SelfPaced/ContentViewer.js
// ──────────────────────────────────────────────────────────────────────────────
// Universal content viewer — renders documents (PDF, PPT, DOCX, images, text)
// inline within the LMS. Supports light/dark theme, fully responsive.
//
// Supported types:
//   • video      → handled externally by ReactPlayer (not in this component)
//   • pdf        → react-pdf (pdf.js) with page navigation + zoom
//   • ppt / pptx → @kandiforge/pptx-renderer (canvas-based, client-side)
//   • docx       → docx-preview (client-side DOCX → HTML rendering)
//   • text       → Rich HTML or plain text rendering
//   • image      → Native <img> with zoom
//   • quiz       → handled externally by QuizPlayer (not in this component)
//   • fallback   → Download link for unsupported types
//
// Content protection (vc_be/utils/contentAccess.js decides; this obeys):
//   canDownload=false → the file is fetched ONCE into memory and rendered from
//                       a blob, so no URL reaches the DOM; every "open in new
//                       tab" / download affordance is gone; spreadsheets render
//                       here instead of through Office Online, which would need
//                       a public URL.
//   canCopy=false     → no PDF text layer, and selection, copy, drag and the
//                       save / print shortcuts are blocked inside the viewer.
// ──────────────────────────────────────────────────────────────────────────────

import React, { useEffect, useState, useRef } from 'react';
import { Spinner } from 'react-bootstrap';
import { Document, Page } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import {
  ChevronLeft, ChevronRight, ChevronUp, ChevronDown,
  ZoomIn, ZoomOut, Maximize2, Minimize2,
  Download, RotateCw, FileText, AlertCircle, Image as ImageIcon,
  Sparkles, Play, Pause,
} from 'lucide-react';
import { AppButton, EmptyState } from '../ui';
import { getAIExplanations } from '../../utils/selfPacedApi';
import { parsePPTX, PPTXViewer } from '@kandiforge/pptx-renderer';
import FullscreenViewer from './FullscreenViewer';
import SheetTable from './SheetTable';
import ContentWatermark from '../Common/ContentWatermark';
import useCopyGuard from '../../hooks/useCopyGuard';
import { PDF_OPTIONS, usePdfSource } from './pdfSource';
import './_fullscreen-viewer.scss';

// ═════════════════════════════════════════════════════════════════════════════
// Auto-complete helper — fires onComplete once after a short viewing delay
// Used by text, docx, and image viewers where there's no pagination
// ═════════════════════════════════════════════════════════════════════════════
const AutoCompleteOnView = ({ onComplete, completedRef, delay = 5000 }) => {
  useEffect(() => {
    if (!onComplete || completedRef.current) return;
    const timer = setTimeout(() => {
      if (!completedRef.current) {
        completedRef.current = true;
        onComplete();
      }
    }, delay);
    return () => clearTimeout(timer);
  }, [onComplete, completedRef, delay]);
  return null;
};

// ═════════════════════════════════════════════════════════════════════════════
// Content protection helpers
// ═════════════════════════════════════════════════════════════════════════════

/**
 * For a view-only file: fetch the (short-lived, signed) URL once and hand the
 * viewers an in-memory blob URL instead. A blob URL means nothing outside this
 * tab, so there is no link on the page to copy, share or open elsewhere.
 */
function useProtectedSource(url, enabled) {
  // `forUrl` ties the result to the URL it was made from, so a render that
  // happens before the effect below never hands the raw URL to a viewer.
  const [state, setState] = useState({ forUrl: null, src: null, error: false });

  useEffect(() => {
    if (!enabled || !url) return undefined;
    let cancelled = false;
    let objectUrl = null;
    (async () => {
      try {
        const resp = await fetch(url, { credentials: 'omit', cache: 'no-store' });
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const blob = await resp.blob();
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setState({ forUrl: url, src: objectUrl, error: false });
      } catch {
        if (!cancelled) setState({ forUrl: url, src: null, error: true });
      }
    })();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [url, enabled]);

  if (!enabled || !url) return { src: url, error: false };
  if (state.forUrl !== url) return { src: null, error: false };
  return state;
}

/** The toolbar's download action — only for a file the student may download. */
const ToolbarDownload = ({ onDownload }) => (
  <button type="button" onClick={onDownload} title="Download" aria-label="Download">
    <Download size={14} />
  </button>
);

// ═════════════════════════════════════════════════════════════════════════════
// AI Explanation Block — inline per-page explanations with TTS
// ═════════════════════════════════════════════════════════════════════════════
const AIExplanationBlock = ({ lessonId, pageNumber, onTotalPages }) => {
  const [explanation, setExplanation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(true);
  const [activated, setActivated] = useState(false);
  const [ttsState, setTtsState] = useState('idle'); // idle | playing | paused
  const cacheRef = useRef({});
  const totalRef = useRef(0);

  // Reset everything when lesson changes
  useEffect(() => {
    cacheRef.current = {};
    totalRef.current = 0;
    setActivated(false);
    setExplanation(null);
    setLoading(false);
    setExpanded(true);
    window.speechSynthesis.cancel();
    setTtsState('idle');
  }, [lessonId]);

  // Sync explanation when outer pageNumber changes
  useEffect(() => {
    if (activated && totalRef.current > 0) {
      setExplanation(cacheRef.current[pageNumber] ?? null);
    }
    window.speechSynthesis.cancel();
    setTtsState('idle');
  }, [pageNumber, activated]);

  // Cleanup TTS on unmount
  useEffect(() => () => window.speechSynthesis.cancel(), []);

  const handleExplain = async () => {
    setActivated(true);
    setExpanded(true);
    // If already cached, just look up
    if (totalRef.current > 0) {
      setExplanation(cacheRef.current[pageNumber] ?? null);
      return;
    }
    setLoading(true);
    try {
      const res = await getAIExplanations(lessonId, { page: 1, limit: 200 });
      const data = res.data?.data || res.data;
      const arr = Array.isArray(data) ? data : (data?.explanations || []);
      arr.forEach(e => { cacheRef.current[e.pageNumber] = e.explanation || e.explanationText; });
      totalRef.current = arr.length;
      if (onTotalPages) onTotalPages(arr.length);
      setExplanation(cacheRef.current[pageNumber] ?? null);
    } catch {
      setExplanation(null);
    } finally {
      setLoading(false);
    }
  };

  // ── TTS handlers ──
  const speak = (text) => {
    window.speechSynthesis.cancel();
    const utter = new SpeechSynthesisUtterance(text);
    utter.onend = () => setTtsState('idle');
    window.speechSynthesis.speak(utter);
    setTtsState('playing');
  };

  const handlePause = () => {
    window.speechSynthesis.pause();
    setTtsState('paused');
  };

  const handleResume = () => {
    window.speechSynthesis.resume();
    setTtsState('playing');
  };

  // ── Render ──
  if (!activated) {
    return (
      <div className="ai-explanation__trigger-wrap">
        <button className="ai-explanation__trigger" onClick={handleExplain}>
          <Sparkles size={14} /> Explain with AI
        </button>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="ai-explanation ai-explanation--loading">
        <Spinner animation="border" size="sm" variant="secondary" />
        <span>Generating explanation\u2026</span>
      </div>
    );
  }

  if (!explanation) {
    return (
      <div className="ai-explanation ai-explanation--empty">
        AI explanation not available for this page.
      </div>
    );
  }

  return (
    <div className="ai-explanation">
      <button className="ai-explanation__header" onClick={() => setExpanded(e => !e)}>
        <span className="ai-explanation__header-title">
          <Sparkles size={14} /> AI Explanation
          {totalRef.current > 1 && <span className="ai-explanation__page-label"> \u2014 Page {pageNumber}</span>}
        </span>
        {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
      </button>
      {expanded && (
        <div className="ai-explanation__body">
          <div className="ai-explanation__text">{explanation}</div>
          {'speechSynthesis' in window && (
            <div className="ai-explanation__tts">
              {ttsState === 'idle' && (
                <button className="ai-explanation__tts-btn" onClick={() => speak(explanation)}>
                  <Play size={13} /> Listen
                </button>
              )}
              {ttsState === 'playing' && (
                <button className="ai-explanation__tts-btn" onClick={handlePause}>
                  <Pause size={13} /> Pause
                </button>
              )}
              {ttsState === 'paused' && (
                <button className="ai-explanation__tts-btn" onClick={handleResume}>
                  <Play size={13} /> Resume
                </button>
              )}
              {ttsState !== 'idle' && (
                <button className="ai-explanation__tts-btn" onClick={() => speak(explanation)}>
                  <RotateCw size={13} /> Replay
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ═════════════════════════════════════════════════════════════════════════════
// PDF Viewer
// ═════════════════════════════════════════════════════════════════════════════
const PdfViewer = ({ url, title, lessonId, onComplete, canDownload = true, canCopy = true, onDownload }) => {
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const completedRef = useRef(false);
  const [scale, setScale] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const containerRef = useRef(null);
  const { file: pdfFile, onLoadError, failed: loadError } = usePdfSource(url);

  const onDocumentLoadSuccess = ({ numPages: total }) => {
    setNumPages(total);
    setPageNumber(1);
  };

  // Auto-complete when student reaches the last page
  useEffect(() => {
    if (numPages && pageNumber >= numPages && !completedRef.current && onComplete) {
      completedRef.current = true;
      onComplete();
    }
  }, [pageNumber, numPages, onComplete]);

  const goPage = (delta) => {
    setPageNumber(p => Math.min(Math.max(1, p + delta), numPages || 1));
  };

  const adjustZoom = (delta) => {
    setScale(s => Math.min(Math.max(0.5, s + delta), 3));
  };

  // Track container width for responsive page sizing
  const [containerWidth, setContainerWidth] = useState(800);
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const obs = new ResizeObserver(entries => {
      for (const e of entries) setContainerWidth(e.contentRect.width);
    });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const pageWidth = Math.min(containerWidth - 32, 900) * scale;

  if (loadError) {
    return (
      <div className="cv-fallback">
        <AlertCircle size={28} />
        <p>Unable to render this PDF inline.</p>
        {canDownload && (
          <AppButton size="sm" variant="outline" onClick={() => window.open(url, '_blank')}>
            <Download size={14} /> Open in new tab
          </AppButton>
        )}
      </div>
    );
  }

  return (
    <div className="cv-pdf" ref={containerRef}>
      {/* Toolbar */}
      <div className="cv-toolbar">
        <div className="cv-toolbar__nav">
          <button onClick={() => goPage(-1)} disabled={pageNumber <= 1} title="Previous page">
            <ChevronLeft size={16} />
          </button>
          <span className="cv-toolbar__page">
            {pageNumber} / {numPages || '–'}
          </span>
          <button onClick={() => goPage(1)} disabled={pageNumber >= (numPages || 1)} title="Next page">
            <ChevronRight size={16} />
          </button>
        </div>

        <div className="cv-toolbar__zoom">
          <button onClick={() => adjustZoom(-0.15)} disabled={scale <= 0.5} title="Zoom out">
            <ZoomOut size={15} />
          </button>
          <span className="cv-toolbar__zoom-level">{Math.round(scale * 100)}%</span>
          <button onClick={() => adjustZoom(0.15)} disabled={scale >= 3} title="Zoom in">
            <ZoomIn size={15} />
          </button>
          <button onClick={() => setScale(1)} title="Reset zoom">
            <RotateCw size={14} />
          </button>
        </div>

        <div className="cv-toolbar__extra">
          {canDownload && onDownload && <ToolbarDownload onDownload={onDownload} />}
          <button onClick={() => setIsFullscreen(true)} title="Fullscreen view" className="cv-toolbar__btn--fullscreen">
            <Maximize2 size={14} />
          </button>
        </div>
      </div>

      {/* Fullscreen portal viewer */}
      {isFullscreen && (
        <FullscreenViewer
          url={url}
          title={title}
          lessonId={lessonId}
          fileType="pdf"
          onClose={() => setIsFullscreen(false)}
        />
      )}

      {/* Document */}
      <div className="cv-pdf__scroll cv-protected">
        <Document
          file={pdfFile}
          options={PDF_OPTIONS}
          onLoadSuccess={onDocumentLoadSuccess}
          onLoadError={onLoadError}
          loading={
            <div className="cv-loading"><Spinner animation="border" variant="secondary" /></div>
          }
          // While the whole-file fallback downloads, `file` is null.
          noData={
            <div className="cv-loading"><Spinner animation="border" variant="secondary" /></div>
          }
          error={
            <div className="cv-fallback">
              <AlertCircle size={24} />
              <p>Failed to load PDF</p>
            </div>
          }
        >
          {/* No text layer means nothing to select: the page is only pixels. */}
          <Page
            pageNumber={pageNumber}
            width={pageWidth}
            renderAnnotationLayer
            renderTextLayer={canCopy}
          />
        </Document>
      </div>
      {lessonId && <AIExplanationBlock lessonId={lessonId} pageNumber={pageNumber} />}
    </div>
  );
};

// ═════════════════════════════════════════════════════════════════════════════
// DOCX Viewer (docx-preview renders DOCX → HTML in a container)
// ═════════════════════════════════════════════════════════════════════════════
const DocxViewer = ({ url, title, lessonId, onComplete, canDownload = true, onDownload }) => {
  const containerRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const completedRef = useRef(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    if (!url) return;
    let cancelled = false;

    (async () => {
      try {
        setLoading(true);
        setError(false);

        // Fetch the DOCX file as ArrayBuffer
        const response = await fetch(url);
        if (!response.ok) throw new Error('Fetch failed');
        const arrayBuffer = await response.arrayBuffer();

        if (cancelled) return;

        // Dynamically import docx-preview to code-split
        const { renderAsync } = await import('docx-preview');

        if (cancelled || !containerRef.current) return;

        // Clear previous content
        containerRef.current.innerHTML = '';

        // Render DOCX into the container
        await renderAsync(arrayBuffer, containerRef.current, undefined, {
          className: 'cv-docx__rendered',
          inWrapper: true,
          ignoreWidth: false,
          ignoreHeight: false,
          ignoreFonts: false,
          breakPages: true,
          ignoreLastRenderedPageBreak: true,
          experimental: false,
          trimXmlDeclaration: true,
          useBase64URL: true,
        });

        setLoading(false);
      } catch {
        if (!cancelled) {
          setError(true);
          setLoading(false);
        }
      }
    })();

    return () => { cancelled = true; };
  }, [url]);

  if (error) {
    return (
      <div className="cv-fallback">
        <AlertCircle size={28} />
        <p>Unable to render this document inline.</p>
        {canDownload && (
          <AppButton size="sm" variant="outline" onClick={() => window.open(url, '_blank')}>
            <Download size={14} /> Download Document
          </AppButton>
        )}
      </div>
    );
  }

  return (
    <div className="cv-docx">
      <div className="cv-toolbar">
        <div className="cv-toolbar__nav">
          <FileText size={15} />
          <span className="cv-toolbar__page">{title || 'Document'}</span>
        </div>
        <div className="cv-toolbar__extra">
          {canDownload && onDownload && <ToolbarDownload onDownload={onDownload} />}
          <button onClick={() => setIsFullscreen(true)} title="Fullscreen view" className="cv-toolbar__btn--fullscreen">
            <Maximize2 size={14} />
          </button>
        </div>
      </div>

      {isFullscreen && (
        <FullscreenViewer
          url={url}
          title={title}
          lessonId={lessonId}
          fileType="docx"
          onClose={() => setIsFullscreen(false)}
        />
      )}

      {loading && (
        <div className="cv-loading"><Spinner animation="border" variant="secondary" /></div>
      )}
      <div
        ref={containerRef}
        className="cv-docx__container cv-protected"
        style={{ display: loading ? 'none' : 'block' }}
      />
      {lessonId && !loading && !error && <AIExplanationBlock lessonId={lessonId} pageNumber={1} />}
      {/* Auto-complete once document finishes rendering */}
      {!loading && !error && <AutoCompleteOnView onComplete={onComplete} completedRef={completedRef} />}
    </div>
  );
};

// ═════════════════════════════════════════════════════════════════════════════
// PPT/PPTX Viewer — parsePPTX → PPTXViewer (canvas-based, client-side)
// ═════════════════════════════════════════════════════════════════════════════
const PptViewer = ({ url, title, lessonId, onComplete, canDownload = true, onDownload }) => {
  const [slideNumber, setSlideNumber] = useState(1);
  const [pptxData, setPptxData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [renderError, setRenderError] = useState(false);
  const completedRef = useRef(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const wrapRef = useRef(null);

  // Track wrapper width for responsive sizing
  const [dims, setDims] = useState({ w: 960, h: 540 });
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const measure = () => {
      const w = el.clientWidth || 960;
      setDims({ w, h: Math.round(w * 9 / 16) });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Fetch PPTX as ArrayBuffer → parse with parsePPTX
  useEffect(() => {
    if (!url) return;
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        setRenderError(false);
        const resp = await fetch(url);
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const buf = await resp.arrayBuffer();
        const data = await parsePPTX(buf);
        if (!cancelled) setPptxData(data);
      } catch (err) {
        console.error('PPTX parse error:', err);
        if (!cancelled) setRenderError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [url]);

  // Auto-complete when student reaches the last slide
  const totalSlides = pptxData?.slides?.length || 0;
  useEffect(() => {
    if (totalSlides > 0 && slideNumber >= totalSlides && !completedRef.current && onComplete) {
      completedRef.current = true;
      onComplete();
    }
  }, [slideNumber, totalSlides, onComplete]);

  // onSlideChange is 0-indexed → convert to 1-indexed
  const handleSlideChange = (slideIdx) => {
    setSlideNumber(slideIdx + 1);
  };

  if (renderError) {
    return (
      <div className="cv-fallback">
        <AlertCircle size={28} />
        <p>Unable to render this presentation.</p>
        {canDownload && (
          <AppButton size="sm" variant="outline" onClick={() => window.open(url, '_blank')}>
            <Download size={14} /> Download Presentation
          </AppButton>
        )}
      </div>
    );
  }

  return (
    <div className="cv-ppt">
      <div className="cv-toolbar">
        <div className="cv-toolbar__nav">
          <FileText size={15} />
          <span className="cv-toolbar__page">{title || 'Presentation'}</span>
          <span className="cv-toolbar__slide-badge">Slide {slideNumber}</span>
        </div>
        <div className="cv-toolbar__extra">
          {canDownload && onDownload && <ToolbarDownload onDownload={onDownload} />}
          <button onClick={() => setIsFullscreen(true)} title="Fullscreen view" className="cv-toolbar__btn--fullscreen">
            <Maximize2 size={14} />
          </button>
        </div>
      </div>

      {isFullscreen && (
        <FullscreenViewer
          url={url}
          title={title}
          lessonId={lessonId}
          fileType="pptx"
          onClose={() => setIsFullscreen(false)}
        />
      )}

      <div className="cv-ppt__renderer-wrap cv-protected" ref={wrapRef}>
        {loading && (
          <div className="cv-ppt__loading">
            <div className="spinner-border spinner-border-sm" />
            <span>Loading presentation…</span>
          </div>
        )}
        {pptxData && (
          <PPTXViewer
            pptxData={pptxData}
            initialSlide={0}
            width={dims.w}
            height={dims.h}
            showFilmstrip={true}
            filmstripPosition="bottom"
            showModeToggle={false}
            onSlideChange={handleSlideChange}
          />
        )}
      </div>

      {/* AI Explanation — auto-synced via onSlideChange */}
      {lessonId && (
        <AIExplanationBlock
          lessonId={lessonId}
          pageNumber={slideNumber}
        />
      )}
    </div>
  );
};

// ═════════════════════════════════════════════════════════════════════════════
// Image Viewer — with zoom and fit controls
// ═════════════════════════════════════════════════════════════════════════════
const ImageViewer = ({ url, title, onComplete, canDownload = true, onDownload }) => {
  const [scale, setScale] = useState(1);
  const [fit, setFit] = useState('contain'); // 'contain' | 'actual'
  const completedRef = useRef(false);

  const adjustZoom = (delta) => {
    setScale(s => Math.min(Math.max(0.25, s + delta), 5));
    setFit('actual');
  };

  return (
    <div className="cv-image">
      <div className="cv-toolbar">
        <div className="cv-toolbar__nav">
          <ImageIcon size={15} />
          <span className="cv-toolbar__page">{title || 'Image'}</span>
        </div>
        <div className="cv-toolbar__zoom">
          <button onClick={() => adjustZoom(-0.2)} disabled={scale <= 0.25} title="Zoom out">
            <ZoomOut size={15} />
          </button>
          <span className="cv-toolbar__zoom-level">{Math.round(scale * 100)}%</span>
          <button onClick={() => adjustZoom(0.2)} disabled={scale >= 5} title="Zoom in">
            <ZoomIn size={15} />
          </button>
          <button onClick={() => { setScale(1); setFit('contain'); }} title="Fit to view">
            {fit === 'contain' ? <Maximize2 size={14} /> : <Minimize2 size={14} />}
          </button>
        </div>
        <div className="cv-toolbar__extra">
          {canDownload && onDownload && <ToolbarDownload onDownload={onDownload} />}
        </div>
      </div>
      <AutoCompleteOnView onComplete={onComplete} completedRef={completedRef} />
      <div className="cv-image__scroll cv-protected">
        <img
          src={url}
          alt={title || 'Lesson image'}
          className="cv-image__img"
          style={fit === 'contain'
            ? { maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }
            : { transform: `scale(${scale})`, transformOrigin: 'top left' }
          }
          draggable={false}
          onContextMenu={(e) => e.preventDefault()}
        />
      </div>
    </div>
  );
};

// ═════════════════════════════════════════════════════════════════════════════
// Text / HTML Viewer
// ═════════════════════════════════════════════════════════════════════════════
const TextViewer = ({ content, html, url, title, lessonId, onComplete, canDownload = true }) => {
  const [fetchedContent, setFetchedContent] = useState(null);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [fetchError, setFetchError] = useState('');
  const [wordWrap, setWordWrap] = useState(true);
  const completedRef = useRef(false);

  // Fetch text from URL when no inline content/html is provided
  useEffect(() => {
    if (content || html || !url) return;
    let cancelled = false;
    const fetchText = async () => {
      setFetchLoading(true);
      setFetchError('');
      try {
        const res = await fetch(url);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const text = await res.text();
        if (!cancelled) setFetchedContent(text);
      } catch {
        if (!cancelled) setFetchError('Failed to load file content.');
      } finally {
        if (!cancelled) setFetchLoading(false);
      }
    };
    fetchText();
    return () => { cancelled = true; };
  }, [url, content, html]);

  const displayContent = content || fetchedContent;
  const lines = displayContent ? displayContent.split('\n') : [];

  return (
    <div className="cv-text">
      <div className="cv-toolbar">
        <div className="cv-toolbar__nav">
          <FileText size={15} />
          <span className="cv-toolbar__page">{title || 'Text Content'}</span>
          {lines.length > 0 && (
            <span className="cv-toolbar__page" style={{ opacity: 0.6, marginLeft: 8 }}>
              {lines.length} lines
            </span>
          )}
        </div>
        <div className="cv-toolbar__extra">
          {displayContent && (
            <button
              onClick={() => setWordWrap(w => !w)}
              title={wordWrap ? 'Disable word wrap' : 'Enable word wrap'}
              style={{ fontSize: '0.75rem', padding: '2px 8px', opacity: 0.7 }}
            >
              {wordWrap ? 'No Wrap' : 'Wrap'}
            </button>
          )}
        </div>
      </div>
      <div className="cv-text__body">
        {fetchLoading ? (
          <div className="cv-text__loading">
            <Spinner animation="border" size="sm" variant="secondary" />
            <span>Loading file content…</span>
          </div>
        ) : fetchError ? (
          <div className="cv-text__error">
            <AlertCircle size={16} />
            <span>{fetchError}</span>
            {url && canDownload && (
              <AppButton size="sm" variant="outline" onClick={() => window.open(url, '_blank')}>
                <Download size={14} /> Download Instead
              </AppButton>
            )}
          </div>
        ) : html ? (
          <div className="cv-text__html" dangerouslySetInnerHTML={{ __html: html }} />
        ) : displayContent ? (
          <div className={`cv-text__code ${wordWrap ? 'cv-text__code--wrap' : ''}`}>
            <table className="cv-text__code-table">
              <tbody>
                {lines.map((line, i) => (
                  <tr key={i}>
                    <td className="cv-text__line-num">{i + 1}</td>
                    <td className="cv-text__line-content">{line || ' '}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="cv-text__plain">No content available.</div>
        )}
      </div>
      {lessonId && <AIExplanationBlock lessonId={lessonId} pageNumber={1} />}
      {/* Auto-complete once text content is visible */}
      {!fetchLoading && !fetchError && (content || html || fetchedContent) && (
        <AutoCompleteOnView onComplete={onComplete} completedRef={completedRef} />
      )}
    </div>
  );
};

// ═════════════════════════════════════════════════════════════════════════════
// Spreadsheet Viewer (XLS/XLSX) — Microsoft Office Online, or rendered here
// ═════════════════════════════════════════════════════════════════════════════
// Office Online fetches the file itself, so it needs a URL Microsoft's servers
// can reach — and its viewer offers a download. A view-only spreadsheet is
// therefore rendered in the browser from the in-memory copy instead.
const SpreadsheetViewer = ({ url, title, canDownload = true, onDownload }) => {
  const renderLocally = !canDownload || String(url).startsWith('blob:');
  const officeUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`;

  return (
    <div className="cv-ppt">
      <div className="cv-toolbar">
        <div className="cv-toolbar__nav">
          <FileText size={15} />
          <span className="cv-toolbar__page">{title || 'Spreadsheet'}</span>
        </div>
        <div className="cv-toolbar__extra">
          {canDownload && onDownload && <ToolbarDownload onDownload={onDownload} />}
          {!renderLocally && (
            <button onClick={() => window.open(officeUrl, '_blank')} title="Open in new tab">
              <Maximize2 size={14} />
            </button>
          )}
        </div>
      </div>
      {renderLocally ? (
        <SheetTable url={url} />
      ) : (
        <div className="cv-ppt__frame-wrap cv-protected">
          <iframe
            src={officeUrl}
            title={title || 'Spreadsheet'}
            className="cv-ppt__iframe"
            sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
            allowFullScreen
          />
        </div>
      )}
    </div>
  );
};

// ═════════════════════════════════════════════════════════════════════════════
// Download Fallback — for unsupported types
// ═════════════════════════════════════════════════════════════════════════════
const DownloadFallback = ({ url, title, fileName, canDownload = true }) => (
  <div className="cv-fallback">
    <FileText size={36} />
    <h4>{title || 'File'}</h4>
    {fileName && <p className="cv-fallback__name">{fileName}</p>}
    {canDownload ? (
      <>
        <p>This file type cannot be previewed inline.</p>
        <AppButton size="sm" onClick={() => window.open(url, '_blank')}>
          <Download size={14} /> Download File
        </AppButton>
      </>
    ) : (
      <p>This file is view-only, and this type can't be opened in the browser. Ask your trainer for a PDF copy.</p>
    )}
  </div>
);

// ═════════════════════════════════════════════════════════════════════════════
// Main ContentViewer — routes to the correct sub-viewer
// ═════════════════════════════════════════════════════════════════════════════
/**
 * @param {Object} props
 * @param {string} props.type         — lesson.type ('pdf','ppt','docx','text','video','quiz')
 * @param {string} props.fileType     — lesson.fileType ('PDF','PPT','PPTX','DOCX','DOC','XLS','XLSX','JPG','PNG','MP4',…)
 * @param {string} [props.url]        — presigned S3 URL for the file
 * @param {string} [props.content]    — text content (for type='text')
 * @param {string} [props.html]       — HTML content (for type='text')
 * @param {string} [props.title]      — display title
 * @param {string} [props.fileName]   — original file name
 * @param {boolean} [props.loading]   — external loading state
 * @param {Function} [props.onComplete] — called once when the lesson should be auto-marked complete
 * @param {boolean} [props.canDownload=true] — false: in-memory rendering, no download/open affordances
 * @param {boolean} [props.canCopy=true]     — false: no text layer, no selecting/copying/printing
 * @param {Function} [props.onDownload]      — shows a Download action in the toolbar (only if canDownload)
 */
export default function ContentViewer({
  type, fileType, url, pdfUrl, content, html, title, fileName, loading, lessonId, onComplete,
  canDownload = true, canCopy = true, onDownload,
}) {
  const rootRef = useRef(null);

  // Normalize file type to uppercase
  const ft = (fileType || '').toUpperCase();
  const lessonType = (type || '').toLowerCase();

  // Determine which viewer to use
  // Priority: explicit fileType > lesson type
  const resolvedType = resolveViewerType(lessonType, ft, url, fileName);

  // If server has converted PPT to PDF, use PDF viewer for better control
  const renderPdfForPpt = resolvedType === 'ppt' && !!pdfUrl;
  const rawSource = renderPdfForPpt ? pdfUrl : url;
  const { src, error: sourceError } = useProtectedSource(
    rawSource,
    !canDownload && resolvedType !== 'download' && !loading,
  );
  const ready = !loading && !sourceError && !(rawSource && !src);
  // Scoped to the viewer, so it only starts once the viewer (and its ref) exists.
  useCopyGuard({ active: !canCopy && ready, scopeRef: rootRef });

  // Show spinner while content (or the protected copy of it) is loading
  if (!ready && !sourceError) {
    return (
      <div className="cv-wrapper cv-wrapper--loading">
        <Spinner animation="border" variant="secondary" />
      </div>
    );
  }

  if (sourceError) {
    return (
      <div className="cv-wrapper">
        <div className="cv-fallback">
          <AlertCircle size={28} />
          <p>This file could not be opened. Close it and try again.</p>
        </div>
      </div>
    );
  }

  const protect = { canDownload, canCopy, onDownload };
  // The watermark marks content, so it goes wherever there is some.
  const watermark = resolvedType === 'text' || (resolvedType !== 'download' && !!src);
  let body;

  switch (resolvedType) {
    case 'pdf':
      body = src
        ? <PdfViewer url={src} title={title} lessonId={lessonId} onComplete={onComplete} {...protect} />
        : <EmptyState title="No PDF" description="PDF file not available." />;
      break;

    case 'docx':
      body = src
        ? <DocxViewer url={src} title={title} lessonId={lessonId} onComplete={onComplete} {...protect} />
        : <EmptyState title="No Document" description="Document file not available." />;
      break;

    case 'ppt':
      if (renderPdfForPpt) {
        body = <PdfViewer url={src} title={title} lessonId={lessonId} onComplete={onComplete} {...protect} />;
      } else {
        body = src
          ? <PptViewer url={src} title={title} lessonId={lessonId} onComplete={onComplete} {...protect} />
          : <EmptyState title="No Presentation" description="Presentation file not available." />;
      }
      break;

    case 'xls':
      body = src
        ? <SpreadsheetViewer url={src} title={title} {...protect} />
        : <EmptyState title="No Spreadsheet" description="Spreadsheet file not available." />;
      break;

    case 'image':
      body = src
        ? <ImageViewer url={src} title={title} onComplete={onComplete} {...protect} />
        : <EmptyState title="No Image" description="Image file not available." />;
      break;

    case 'text':
      body = <TextViewer content={content} html={html} url={src} title={title} lessonId={lessonId} onComplete={onComplete} canDownload={canDownload} />;
      break;

    case 'download':
    default:
      body = url
        ? <DownloadFallback url={url} title={title} fileName={fileName} canDownload={canDownload} />
        : <EmptyState title="No content" description="This lesson has no viewable content yet." />;
  }

  return (
    <div className="cv-wrapper" ref={rootRef}>
      {watermark && <ContentWatermark />}
      {body}
    </div>
  );
}

/**
 * Resolves which viewer type to use based on lesson type, file type, url, and file name.
 */
function resolveViewerType(lessonType, ft, url, fileName) {
  // File-type based detection (most reliable)
  if (ft === 'PDF') return 'pdf';
  if (ft === 'PPT' || ft === 'PPTX') return 'ppt';
  if (ft === 'DOC' || ft === 'DOCX') return 'docx';
  if (ft === 'XLS' || ft === 'XLSX') return 'xls';
  if (['JPG', 'JPEG', 'PNG', 'GIF', 'WEBP', 'SVG', 'BMP'].includes(ft)) return 'image';
  if (ft === 'TXT' || ft === 'MD') return 'text';

  // Lesson-type based detection
  if (lessonType === 'pdf') return 'pdf';
  if (lessonType === 'ppt' || lessonType === 'presentation') return 'ppt';
  if (lessonType === 'docx' || lessonType === 'document') return 'docx';
  if (lessonType === 'text') return 'text';
  if (lessonType === 'image') return 'image';

  // URL/filename extension based detection
  const ext = getExtension(url) || getExtension(fileName);
  if (ext) {
    if (ext === 'pdf') return 'pdf';
    if (ext === 'ppt' || ext === 'pptx') return 'ppt';
    if (ext === 'doc' || ext === 'docx') return 'docx';
    if (ext === 'xls' || ext === 'xlsx') return 'xls';
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'].includes(ext)) return 'image';
    if (ext === 'txt' || ext === 'md') return 'text';
  }

  return 'download';
}

function getExtension(str) {
  if (!str) return '';
  // Strip query params, then get extension
  const clean = str.split('?')[0].split('#')[0];
  const dot = clean.lastIndexOf('.');
  return dot >= 0 ? clean.substring(dot + 1).toLowerCase() : '';
}
